import { useEffect, useState, useCallback, useRef } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { motion, AnimatePresence } from "framer-motion";

interface BreathingPromptsProps {
  audioSrc: string;
  isPlaying: boolean;
  hasStartedPlaying: boolean;
  onControlsDisabledChange?: (disabled: boolean) => void;
}

export default function BreathingPrompts({
  isPlaying,
  hasStartedPlaying,
  onControlsDisabledChange,
}: BreathingPromptsProps) {
  const [currentPrompt, setCurrentPrompt] = useState("");
  const [sequenceStarted, setSequenceStarted] = useState(false);
  const intl = useIntl();
  const cleanupRef = useRef<(() => void) | null>(null);

  const runInhaleExhaleSequence = useCallback(() => {
    const breathingSequence = [ 
      { textId: "breathing.prompts.inhale", duration: 3260 },
      { textId: "breathing.prompts.exhale", duration: 3270 },
    ];

    let timeoutId: NodeJS.Timeout | null = null;
    let currentStep = 0;
    let currentCycle = 0;

    const runSequence = () => {
      if (currentCycle >= 3) {
        
        setCurrentPrompt(
          intl.formatMessage({ id: "breathing.prompts.breatheContinue" })
        );
        // Keep controls disabled during "continue breath" phase
        return;
      }

      const prompt = breathingSequence[currentStep];
      setCurrentPrompt(intl.formatMessage({ id: prompt.textId }));

      timeoutId = setTimeout(() => {
        currentStep = (currentStep + 1) % 2;
        if (currentStep === 0) {
          currentCycle++;
        }
        runSequence();
      }, prompt.duration);
    };

    runSequence();
    
    // Return cleanup function
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [intl]);

  const startBreathingSequence = useCallback(() => {
    setCurrentPrompt(
      intl.formatMessage({ id: "breathing.prompts.breatheDeeply" })
    );

    setTimeout(() => {
      cleanupRef.current = runInhaleExhaleSequence();
    }, 4500);
  }, [intl, runInhaleExhaleSequence]);

  useEffect(() => {
    if (isPlaying && !sequenceStarted && hasStartedPlaying) {
      setSequenceStarted(true);
      onControlsDisabledChange?.(true);
      setTimeout(() => {
        startBreathingSequence();
      }, 1000);
    }

    if (!isPlaying && sequenceStarted) {
      setSequenceStarted(false);
      setCurrentPrompt("");
      // Clean up any running timeouts
      if (cleanupRef.current) {
        cleanupRef.current();
        cleanupRef.current = null;
      }
      onControlsDisabledChange?.(false);
    }
  }, [
    isPlaying,
    hasStartedPlaying,
    sequenceStarted,
    onControlsDisabledChange,
    startBreathingSequence,
  ]);

  return (
    <div className="space-y-6">
      <div className="text-center">
        <AnimatePresence mode="wait">
          {!hasStartedPlaying && (
            <div className="text-center py-8">
              <p className="text-lg text-white/50">
                <FormattedMessage id="breathing.prompts.pressPlay" />
              </p>
            </div>
          )}

          {hasStartedPlaying && currentPrompt && (
            <motion.div
              key={currentPrompt}
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 1.1 }}
              transition={{
                duration: 0.4,
                ease: [0.25, 0.46, 0.45, 0.94],
              }}
              className="space-y-2"
            >
              <div className="text-4xl font-bold text-center py-6 text-black">
                {currentPrompt}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
